import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { DateQuickButtons, DateRangeField, currentYearRange } from '../../components/ui/DateField'
import { Pagination, type PageSize } from '../../components/ui/Pagination'
import { ExportDropdown } from '../../components/ui/ExportDropdown'
import { downloadXlsx } from '../../lib/downloadXlsx'
import { useAuthStore } from '../../stores/auth'

interface EmagCustomerInvoice {
  id: number
  number: string
  emagOrderId: string | null
  date: string
  isStorno: boolean
  reversalFor: string | null
  customerName: string | null
  customerCif: string | null
  totalNoVat: string
  totalVat: string
  totalWithVat: string
  currency: string
}

export function FacturiVanzareEmagPage() {
  /** Exportul e un drept PERSONAL: nu vine cu niciun rol, nici cu `admin`. */
  const poateExporta = useAuthStore((s) => s.permissions).includes('date.clienti:export')
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState<PageSize>(50)
  const [search, setSearch] = useState('')
  const [dateFrom, setDateFrom] = useState(currentYearRange().from)
  const [dateTo, setDateTo] = useState(currentYearRange().to)

  const { data, isLoading } = useQuery<{ invoices: EmagCustomerInvoice[]; total: number }>({
    queryKey: ['emag-customer-invoices', page, pageSize, search, dateFrom, dateTo],
    queryFn: async () => {
      const params = new URLSearchParams()
      params.set('limit', String(pageSize))
      params.set('offset', String(page * pageSize))
      if (search.trim()) params.set('search', search.trim())
      if (dateFrom) params.set('dateFrom', dateFrom)
      if (dateTo) params.set('dateTo', dateTo)
      return (await api.get(`/emag/customer-invoices?${params}`)).data
    },
  })

  const [exporting, setExporting] = useState(false)
  const exportInvoices = async (currentPageOnly: boolean) => {
    try {
      setExporting(true)
      const params = new URLSearchParams()
      if (currentPageOnly) {
        params.set('limit', String(pageSize))
        params.set('offset', String(page * pageSize))
      }
      if (search.trim()) params.set('search', search.trim())
      if (dateFrom) params.set('dateFrom', dateFrom)
      if (dateTo) params.set('dateTo', dateTo)
      await downloadXlsx(`/emag/customer-invoices/export?${params.toString()}`)
      toast.success('Export descărcat')
    } catch {
      toast.error('Eroare la export')
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">
          eMAG → Facturi vânzare (de la Boost4Life către clienții eMAG)
        </h1>
        {/*
          Butonul dispare pentru cine n-are dreptul. Nu e protectie — aceea e pe ruta, care refuza
          oricum — ci politete: sa nu apesi ceva ce vine inapoi cu „interzis".
        */}
        {poateExporta && (
          <ExportDropdown
            loading={exporting}
            pageCount={data?.invoices?.length}
            totalCount={data?.total}
            onExportPage={() => exportInvoices(true)}
            onExportAll={() => exportInvoices(false)}
          />
        )}
      </div>

      <Card title="Filtre">
        <DateQuickButtons
          fromValue={dateFrom}
          toValue={dateTo}
          onChange={(f, t) => {
            setDateFrom(f)
            setDateTo(t)
          }}
        />
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
          <Field
            label="Caută după număr sau client"
            placeholder="DSMF... sau nume"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="md:col-span-2">
            <DateRangeField
              fromValue={dateFrom}
              toValue={dateTo}
              onChange={(f, t) => {
                setDateFrom(f)
                setDateTo(t)
              }}
            />
          </div>
        </div>
      </Card>

      <Card>
        {isLoading && <p className="text-sm text-b4l-text-muted">Se încarcă…</p>}
        {data?.invoices?.length === 0 && (
          <p className="text-sm text-b4l-text-muted">Nicio factură găsită.</p>
        )}
        {data?.invoices && data.invoices.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="border-b border-b4l-border text-left text-b4l-text-muted">
                <tr>
                  <th className="py-2 pr-3">Număr</th>
                  <th className="py-2 pr-3">Data</th>
                  <th className="py-2 pr-3">Comandă eMAG</th>
                  <th className="py-2 pr-3">Client</th>
                  <th className="py-2 pr-3">CIF</th>
                  <th className="py-2 pr-3 text-right">Fără TVA</th>
                  <th className="py-2 pr-3 text-right">TVA</th>
                  <th className="py-2 pr-3 text-right">Total</th>
                  <th className="py-2 pr-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.invoices.map((i) => (
                  <tr key={i.id} className="border-b border-b4l-border">
                    <td className="py-2 pr-3 font-mono">{i.number}</td>
                    <td className="py-2 pr-3">{new Date(i.date).toLocaleDateString('en-GB')}</td>
                    <td className="py-2 pr-3 font-mono">{i.emagOrderId ?? '—'}</td>
                    <td className="py-2 pr-3">{i.customerName ?? '—'}</td>
                    <td className="py-2 pr-3 font-mono">{i.customerCif ?? '—'}</td>
                    <td className="py-2 pr-3 text-right">{Number(i.totalNoVat).toFixed(2)}</td>
                    <td className="py-2 pr-3 text-right">{Number(i.totalVat).toFixed(2)}</td>
                    <td className="py-2 pr-3 text-right font-medium">
                      {Number(i.totalWithVat).toFixed(2)} {i.currency}
                    </td>
                    <td className="py-2 pr-3">
                      {i.isStorno ? (
                        <span className="rounded bg-b4l-red-light px-1 text-b4l-red-dark">
                          STORNO {i.reversalFor ? `← ${i.reversalFor}` : ''}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data?.total != null && (
          <div className="mt-3">
            <Pagination
              page={page}
              pageSize={pageSize}
              total={data.total}
              onPageChange={setPage}
              onPageSizeChange={(s) => {
                setPageSize(s)
                setPage(0)
              }}
              itemLabel="facturi"
            />
          </div>
        )}
      </Card>
    </div>
  )
}
